import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../utils'
import { Icon } from './icon'

const alertVariants = cva(
  'relative w-full flex gap-2 rounded-md border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive:
          'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
        tip: 'bg-tip/20 border-tip text-tip-foreground',
        warn: 'bg-earth-yellow text-black/80 border-0 font-semibold',
        missing: 'text-center flex-col justify-center items-center',
        success: 'bg-success border-0',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const AlertIcon = (props: { variant?: string | null }) => {
  switch (props.variant) {
    case 'success':
      return (
        <div className="bg-success flex h-8 w-8 items-center rounded-md">
          <Icon name="check" className="stroke-success-foreground ml-px h-5" />
        </div>
      )
    case 'tip':
      return (
        <div className="bg-tip flex h-8 w-8 items-center rounded-md">
          <Icon name="graduation-cap" className="ml-px h-5 stroke-black" />
        </div>
      )
    default:
      return null
  }
}

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof alertVariants> & { actions?: React.ReactNode }
>(({ className, variant, children, actions, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  >
    <AlertIcon variant={variant} />
    <div
      className={cn('grid w-full gap-2', {
        'mt-[6px]': variant && ['success', 'tip'].includes(variant),
      })}
    >
      {children}
    </div>
    <div className="self-center">{actions}</div>
  </div>
))
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('[&_p]:leading-relaxed', className)} {...props} />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription }
